import React from 'react';
import { Button, ProgressStrip } from '../ui';
import { ResearchSummary } from '../../types/bom.types';
import { DoneTally, doneTitleOf, etaPartsOf, isResearchRunning } from './researchProgress';

interface ResearchProgressStripProps {
  summary: ResearchSummary;
  /** Parts on a second or later attempt, counted from the rows. */
  retrying: number;
  tally: DoneTally;
  onDetails: () => void;
  /** Absent for a viewer: retrying is the owner's to do. */
  onRetryFailed?: () => void;
  retryingFailed: boolean;
}

const linkClass = 'font-semibold text-action hover:underline';

/** The research wait once its pop-up is closed, resolving to a tally when it lands. */
export const ResearchProgressStrip: React.FC<ResearchProgressStripProps> = ({
  summary,
  retrying,
  tally,
  onDetails,
  onRetryFailed,
  retryingFailed,
}) => {
  if (!isResearchRunning(summary)) {
    const retryEnd =
      tally.failed > 0 && onRetryFailed ? (
        <Button variant="secondary" size="sm" onClick={onRetryFailed} loading={retryingFailed} loadingLabel="Retrying…">
          Retry failed ({tally.failed})
        </Button>
      ) : undefined;
    return (
      <ProgressStrip
        tone={tally.failed > 0 ? 'attention' : 'done'}
        title={doneTitleOf(tally)}
        counts={{ done: summary.total, running: 0, waiting: 0 }}
        end={retryEnd}
      />
    );
  }

  const eta = etaPartsOf(summary);
  const waiting = Math.max(0, summary.waiting - retrying);
  return (
    <ProgressStrip
      tone={retrying > 0 ? 'attention' : 'running'}
      title={`Finding suppliers · ${summary.done} of ${summary.total} done`}
      counts={{ done: summary.done, running: summary.researching, waiting, attention: retrying }}
      labels={{ waiting: 'waiting', attention: 'trying again' }}
      eta={
        eta ? (
          <>
            About <b className="text-ink">{eta.minutes} min</b> more{eta.doneAround ? ` · around ${eta.doneAround}` : ''}
          </>
        ) : undefined
      }
      end={
        <button type="button" onClick={onDetails} className={linkClass}>
          Details
        </button>
      }
    />
  );
};

export default ResearchProgressStrip;
